import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getClubCollections, formatMoney, type ClubCollections } from "../../services/payments";
import { useFeatures } from "../../hooks/useFeatures";

/**
 * The club's money, from the periods themselves.
 *
 * Four numbers and a list, not a dashboard. A club admin opens this to answer
 * one question — who do I need to call — so the list of who is worst behind is
 * the point and everything else is context for it.
 *
 * Cash and card are split because they settle differently: card money is
 * already in the club's Stripe balance, cash is in somebody's pocket until it
 * is handed over.
 */
export default function ClubCollectionsCard({ clubId }: { clubId: string | null | undefined }) {
  const { features } = useFeatures();
  const navigate = useNavigate();
  const [data, setData] = useState<ClubCollections | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!clubId) return;
    try {
      setData(await getClubCollections(clubId));
    } catch {
      setData(null);
    }
    setLoaded(true);
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  // Part of the module as a whole, not of the card rail: these are the club's
  // own figures, cash included.
  if (!features.paymentsModule) return null;
  if (!loaded || !data) return null;

  const nothingHappening =
    data.month.collected_cents === 0 && data.outstanding_cents === 0 && data.open_periods === 0;
  if (nothingHappening) return null;

  return (
    <div className="p-5 bg-white border shadow-sm border-slate-200 rounded-2xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-800">Încasări</h3>
          <p className="mt-0.5 text-sm text-slate-500">Cotizațiile clubului, pe perioade.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/dashboard/admin-club/receivables")}
          className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
        >
          Toate restanțele
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4 lg:grid-cols-4">
        <Figure
          label="Încasat luna asta"
          value={formatMoney(data.month.collected_cents)}
          hint={
            data.month.collected_cents > 0
              ? `card ${formatMoney(data.month.card_cents)} · numerar ${formatMoney(data.month.cash_cents)}`
              : undefined
          }
          tone="emerald"
        />
        <Figure label="De încasat" value={formatMoney(data.outstanding_cents)} hint={`${data.open_periods} perioade`} />
        <Figure
          label="Restant"
          value={formatMoney(data.overdue_cents)}
          hint={`${data.overdue_periods} perioade`}
          tone={data.overdue_cents > 0 ? "rose" : undefined}
        />
        <Figure label="Sportivi cu datorii" value={String(data.athletes_owing)} />
      </div>

      {data.worst.length > 0 && (
        <>
          <p className="mt-5 text-xs font-semibold tracking-wide uppercase text-slate-400">
            Cele mai mari restanțe
          </p>
          <div className="mt-2 divide-y divide-slate-100">
            {data.worst.slice(0, 8).map((row) => (
              <button
                key={row.athlete_id}
                type="button"
                onClick={() => navigate(`/admin/users/${row.athlete_id}`)}
                className="flex items-center w-full gap-3 py-2.5 text-left hover:bg-slate-50"
              >
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold truncate text-slate-800">
                    {row.athlete_name || "Sportiv"}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {row.team_name || "Fără echipă"} · {row.periods}{" "}
                    {row.periods === 1 ? "perioadă" : "perioade"}
                    {row.days_late > 0 ? ` · ${row.days_late} zile întârziere` : ""}
                  </span>
                </span>
                <span
                  className={`text-sm font-extrabold tabular-nums ${
                    row.days_late > 0 ? "text-rose-600" : "text-slate-700"
                  }`}
                >
                  {formatMoney(row.outstanding_cents)}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "emerald" | "rose";
}) {
  const valueClass =
    tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : "text-slate-800";
  return (
    <div className="p-3 rounded-xl bg-slate-50">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`mt-0.5 text-lg font-extrabold tabular-nums ${valueClass}`}>{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}
